import React, { useState, useCallback, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { IKeyword } from '../models/IKeyword';
import IKeywordSearchDropdownProps from '../models/IKeywordSearchDropdownProps';

const KeywordSearchDropdown: React.FC<IKeywordSearchDropdownProps> = ({
  getSelectedKeywords,
  label,
  keywordsData,
  initialValue,
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<IKeyword[]>(
    initialValue || [],
  );
  const [filteredKeywordsData, setFilterdKeywordsData] = useState<IKeyword[]>(
    [],
  );

  useEffect(() => {
    setFilterdKeywordsData(
      Array.isArray(keywordsData)
        ? keywordsData
            .filter((keyword: IKeyword) => typeof keyword.title === 'string')
            .sort((a, b) => a.title.localeCompare(b.title))
        : [],
    );
  }, [keywordsData]);

  useEffect(() => {
    if (initialValue) {
      setSelectedKeywords(initialValue);
    }
  }, [initialValue]);

  // Memoize the keyword change handler
  const handleKeywordChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: IKeyword[]) => {
      setSelectedKeywords(value);
      getSelectedKeywords(value);
    },
    [getSelectedKeywords],
  );

  // Memoize the filter function to prevent recreation on every render
  const memoizedFilterOptions = useCallback(
    (options: IKeyword[], { inputValue }: { inputValue: string }) => {
      return options
        .filter((option) =>
          option.title.toLowerCase().startsWith(inputValue.toLowerCase()),
        )
        .slice(0, 200);
    },
    [],
  );

  return (
    <div style={{ marginTop: '10px' }}>
      <Autocomplete
        multiple
        options={filteredKeywordsData}
        getOptionLabel={(option) => option.title}
        value={selectedKeywords}
        onChange={handleKeywordChange}
        filterOptions={memoizedFilterOptions}
        filterSelectedOptions
        disableListWrap
        renderOption={(props, option) => (
          <li {...props} key={option.keywordId}>
            {option.title}
          </li>
        )}
        renderInput={(params) => {
          const isResearchInterests = label
            ?.toLowerCase()
            .includes('research interests');
          return (
            <TextField
              {...params}
              variant='outlined'
              label={label}
              placeholder={
                isResearchInterests
                  ? 'Search and select research interests'
                  : 'Search and select keywords'
              }
              helperText={
                isResearchInterests
                  ? 'Choose at least one research interest to personalize your Athena experience.'
                  : 'Choose at least one keyword to personalize your Athena experience.'
              }
            />
          );
        }}
      />
    </div>
  );
};
const MemoizedKeywordSearchDropdown = React.memo(KeywordSearchDropdown);

export default MemoizedKeywordSearchDropdown;
